/**
 * @file index.tsx
 *
 * @fileoverview Renders the current search in the form of facet chips.
 */
import React from 'react';
import { getFilterSelectedItems } from '../utils/searchHelpers';
import { Facet, FacetValue } from '~/jobiqo/models/search';
import Link from '~/jobiqo/components/Link';
import { FormattedMessage } from 'react-intl';
import { flattenForChildren } from '~/jobiqo/services/taxonomyHelpers';
import { clearWithChildren } from '../facets/utils/facetHelpers';
import { Chip } from '~/vcl/atoms/01-forms/08-chip';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { UndoAltSolidIcon } from '~/vcl/icons/UndoAltSolid';

export interface CurrentSearchProps {
  /**
   * The facets that are currently selected.
   */
  facets: Facet[];
  /**
   * What should happen when we click a current search item (clears).
   */
  toggleFilter: (
    filter: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  /**
   * The current querystring
   */
  querystring: any;
  /**
   * The current path
   */
  pathname: string;
  /**
   * The path of the search
   */
  searchPath: string;
}

/**
 * Renders the actual filters, only if there are active items.
 */
const getChips = (facets, toggleFilter, querystring) => {
  if (!facets) {
    return null;
  }
  return facets
    .filter((facet) => {
      const selectedItems = getFilterSelectedItems(querystring, facet);
      return selectedItems.length !== 0;
    })
    .map((facet) => {
      // We need to flat values for this facet. Because some items are children
      // and we might need information from them.
      const flatFacetValues = flattenForChildren(facet.values);
      const selectedItems = getFilterSelectedItems(querystring, facet);
      return selectedItems.map((item) => {
        const facetValue = flatFacetValues.filter((c) => c.filter === item)[0];
        return (
          <Chip
            chipId={item.toString()}
            onRemove={(chip) => {
              // Get the alias of the facet being unselected.
              const facetVal = flatFacetValues.filter(
                (facetValueItem: FacetValue) => {
                  return facetValueItem.filter === chip;
                },
              );

              // if we clear a parent we need to clear children also.
              const clearedItems = clearWithChildren(
                facetValue.filter,
                facet,
                selectedItems.filter((selectedItem) => {
                  return selectedItem !== facetValue.filter;
                }),
              );

              toggleFilter(
                facet.name,
                clearedItems,
                facetVal.length > 0 ? facetVal[0].alias : null,
              );
            }}
            key={item}
          >
            {facetValue.label}
          </Chip>
        );
      });
    });
};

const getFulltextChip = (toggleFilter, querystring) => {
  return querystring.search ? (
    <Chip chipId="search" onRemove={(chip) => toggleFilter('search')}>
      {querystring.search}
    </Chip>
  ) : null;
};

export const CurrentSearch = ({
  facets,
  toggleFilter,
  querystring,
  searchPath,
}: CurrentSearchProps) => {
  // Render the chips to a variable so we can filter properly.
  const facetChips = getChips(facets, toggleFilter, querystring);
  const fulltextChip = getFulltextChip(toggleFilter, querystring);
  // Only show something if we actually have active items.
  return facetChips?.length > 0 || fulltextChip ? (
    <Spacing paddingLeft={6} paddingRight={6}>
      {facetChips}
      {fulltextChip}
      <Spacing />
      <div>
        <Link href={searchPath}>
          <>
            <UndoAltSolidIcon
              aria-hidden="true"
              width="15"
              height="15"
              title="Undo"
            />
            &nbsp;
            <FormattedMessage
              id={'current_search_reset'}
              defaultMessage={'Reset Search'}
            />
          </>
        </Link>
      </div>
    </Spacing>
  ) : null;
};
export default CurrentSearch;
